import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { IFilterResponse, SearchFilter } from 'src/app/infrastructure/iFilter-component';
import { BudgetSearchItem } from 'src/app/model/budget/budget-search-item';
import { AuthService } from 'src/app/services/auth.service';
import { BudgetService } from 'src/app/services/budget/budget.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog.service';
import { MenuService } from 'src/app/services/menu.service';
import { NotificationToastService } from 'src/app/services/notification-toast.service';
import { PagerControlComponent } from '../../controls/pager/pager-control.component';
import { SearchFilterComponent } from '../../search-filter/search-filter.component';

@Component({
  selector: 'app-budget-search',
  templateUrl: './budget-search.component.html',
  styleUrls: ['./budget-search.component.css']
})
export class BudgetSearchComponent implements OnInit, OnDestroy {
  loading: boolean;
  loadingAction: boolean;
  budgetSearchItem: BudgetSearchItem[] = [];
  searchFilters: SearchFilter[];
  columnsDefinition: { field: string; title: string }[] = [
    { field: 'Date', title: 'Fecha generación' },
    { field: 'FinalizeDate', title: 'Fecha vigencia' },
    { field: 'Name', title: 'Nombre' },
    { field: 'Value', title: 'Valor' },
  ];
  displayedColumns: string[] = [...this.columnsDefinition.map(c => c.field), 'Star'];
  dataSource: BudgetSearchItem[] = [];
  columnFilters: { [field: string]: string } = {};
  sortField: string = null;
  sortAsc: boolean = true;
  columnWidths: { [field: string]: number } = {};
  hiddenColumns: Set<string> = new Set();
  dragSourceField: string = null;
  private _columnOrder: string[] = null;
  private _filteredData: BudgetSearchItem[] = [];
  @ViewChild('searchFilter', {static: false}) searchFilter: SearchFilterComponent;
  @ViewChild('pager', {static: true}) pager: PagerControlComponent;

  constructor(public dialog: MatDialog,
    private router: Router,
    private budgetService: BudgetService,
    private menuService: MenuService,
    private confirmDialogService: ConfirmDialogService,
    private notificationToastService: NotificationToastService) {
    this.menuService.setMenuTitle('Presupuesto', '');
  }

  ngOnDestroy(): void {
    (window as any)['__budgetSearchResizeMove'] = null;
    (window as any)['__budgetSearchResizeUp'] = null;
  }

  ngOnInit(): void {
    this.loadColumnState();
  }

  get orderedColumnsDefinition(): { field: string; title: string }[] {
    const order = this._columnOrder != null ? this._columnOrder : this.columnsDefinition.map(c => c.field);
    return order
      .map(f => this.columnsDefinition.find(c => c.field === f))
      .filter(c => c != null && !this.hiddenColumns.has(c.field));
  }

  get availableColumns(): { field: string; title: string }[] {
    const order = this._columnOrder != null ? this._columnOrder : this.columnsDefinition.map(c => c.field);
    return order.map(f => this.columnsDefinition.find(c => c.field === f)).filter(c => c != null);
  }

  get exportColumns(): string[] {
    return this.orderedColumnsDefinition.map(col => col.field);
  }

  get exportColumnsName(): string[] {
    return this.orderedColumnsDefinition.map(col => col.title);
  }

  loadColumnState(): void {
    try {
      const saved = sessionStorage.getItem('budget-search.columns.v1');
      if (saved) {
        const parsed: { order: string[]; hidden: string[] } = JSON.parse(saved);
        this._columnOrder = parsed.order || null;
        this.hiddenColumns = new Set(parsed.hidden || []);
      }
      const savedSort = sessionStorage.getItem('budget-search.sort');
      if (savedSort) {
        const parsedSort: { field: string; asc: boolean } = JSON.parse(savedSort);
        this.sortField = parsedSort.field;
        this.sortAsc = parsedSort.asc;
      }
      const savedWidths = sessionStorage.getItem('budget-search.column-widths.v1');
      if (savedWidths) {
        this.columnWidths = JSON.parse(savedWidths);
      }
    } catch (e) {}
  }

  saveColumnState(): void {
    try {
      sessionStorage.setItem('budget-search.columns.v1', JSON.stringify({
        order: this._columnOrder,
        hidden: Array.from(this.hiddenColumns)
      }));
    } catch (e) {}
  }

  getColumnWidth(field: string): number {
    return this.columnWidths[field] != null ? this.columnWidths[field] : 150;
  }

  startResize(event: MouseEvent, field: string): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.getColumnWidth(field);
    const onMove = (e: MouseEvent) => {
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      this.columnWidths = Object.assign({}, this.columnWidths, { [field]: newWidth });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', (window as any)['__budgetSearchResizeMove']);
      window.removeEventListener('mouseup', (window as any)['__budgetSearchResizeUp']);
      try {
        sessionStorage.setItem('budget-search.column-widths.v1', JSON.stringify(this.columnWidths));
      } catch (e) {}
    };
    (window as any)['__budgetSearchResizeMove'] = onMove;
    (window as any)['__budgetSearchResizeUp'] = onUp;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  sortByColumn(field: string): void {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    try {
      sessionStorage.setItem('budget-search.sort', JSON.stringify({ field: this.sortField, asc: this.sortAsc }));
    } catch (e) {}
    this.applyColumnFilters();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) { return 'unfold_more'; }
    return this.sortAsc ? 'arrow_upward' : 'arrow_downward';
  }

  getSortableValue(element: BudgetSearchItem, field: string): any {
    const value = (element as any)[field];
    if (this.isDateField(field)) {
      return new Date(value).getTime() || 0;
    }
    return value;
  }

  isDateField(field: string): boolean {
    return field === 'Date' || field === 'FinalizeDate';
  }

  formatCellValue(element: BudgetSearchItem, field: string): string {
    const value = (element as any)[field];
    if (this.isDateField(field) && value != null) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        const day = ('0' + d.getDate()).slice(-2);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        return day + '/' + month + '/' + year;
      }
    }
    return String(value != null ? value : '');
  }

  onColumnFilterInput(field: string, value: string): void {
    this.columnFilters = Object.assign({}, this.columnFilters, { [field]: value });
    this.applyColumnFilters();
  }

  applyColumnFilters(): void {
    const dataColumns = this.columnsDefinition.map(c => c.field).filter(f => f !== 'Star');
    let filtered = this._filteredData.filter(element => {
      return dataColumns.every(field => {
        const filterVal = (this.columnFilters[field] || '').toLowerCase().trim();
        if (!filterVal) { return true; }
        const cellVal = this.formatCellValue(element, field).toLowerCase();
        return cellVal.indexOf(filterVal) !== -1;
      });
    });
    if (this.sortField) {
      const sf = this.sortField;
      const asc = this.sortAsc;
      filtered = filtered.slice().sort((a, b) => {
        const va = this.getSortableValue(a, sf);
        const vb = this.getSortableValue(b, sf);
        if (va < vb) { return asc ? -1 : 1; }
        if (va > vb) { return asc ? 1 : -1; }
        return 0;
      });
    }
    this.pager.setItems(filtered);
  }

  onHeaderDragStart(event: DragEvent, field: string): void {
    this.dragSourceField = field;
    event.dataTransfer.effectAllowed = 'move';
  }

  onHeaderDragEnd(): void {
    this.dragSourceField = null;
  }

  onHeaderDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  onHeaderDrop(event: DragEvent, targetField: string): void {
    event.preventDefault();
    if (this.dragSourceField == null || this.dragSourceField === targetField) { return; }
    const order = this._columnOrder != null
      ? this._columnOrder.slice()
      : this.columnsDefinition.map(c => c.field);
    const fromIndex = order.indexOf(this.dragSourceField);
    const toIndex = order.indexOf(targetField);
    if (fromIndex === -1 || toIndex === -1) { return; }
    moveItemInArray(order, fromIndex, toIndex);
    this._columnOrder = order;
    this.saveColumnState();
    this.dragSourceField = null;
  }

  isColumnVisible(field: string): boolean {
    return !this.hiddenColumns.has(field);
  }

  setColumnVisibility(field: string, visible: boolean): void {
    if (visible) {
      this.hiddenColumns.delete(field);
    } else {
      this.hiddenColumns.add(field);
    }
    this.hiddenColumns = new Set(this.hiddenColumns);
    this.saveColumnState();
  }

  showAllColumns(): void {
    this.hiddenColumns = new Set();
    this.saveColumnState();
  }

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = pagedItems;
    this.loading = false;
  }

  onEndSearch(response: IFilterResponse<BudgetSearchItem>) {
    this._filteredData = response.items;
    this.budgetSearchItem = response.items;
    this.searchFilters = response.searchFilters;
    this.applyColumnFilters();
  }

  onStartSearch() {
    this.loading = true;
  }

  add() {
    this.router.navigate(['budget']);
  }

  view(item: BudgetSearchItem) {
    this.router.navigate(['budget-report', item.Group]);
  }

  async disabled(item: BudgetSearchItem) {
    this.confirmDialogService.confirm('Escaner', 'Está seguro que desea deshabilitar el presupuesto?'
    , 'Sí', 'No').subscribe(async (value) => {
      if (value) {
        try {
          this.loadingAction = true;
          await this.budgetService.disabled(item);
          await this.searchFilter.search();
        } catch (err) {
          this.notificationToastService.error(JSON.stringify(err.error), 'Escaner');
        } finally {
          this.loadingAction = false;
        }
      }
    });
  }

  async enabled(item: BudgetSearchItem) {
    this.confirmDialogService.confirm('Escaner', 'Está seguro que desea habilitar el presupuesto?'
    , 'Sí', 'No').subscribe(async (value) => {
      if (value) {
        try {
          this.loadingAction = true;
          await this.budgetService.enabled(item);
          await this.searchFilter.search();
        } catch (err) {
          this.notificationToastService.error(JSON.stringify(err.error), 'Escaner');
        } finally {
          this.loadingAction = false;
        }
      }
    });
  }

}
